import { RuleContext, ValueNode } from '@navstation-portal/reporting/data-model/ui';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { evaluate, readCarryOverField, readReportField, readVesselField } from './evaluate';
import { RuleExplanationLine, explainNode, formatValue } from './explain-rule';
import { resolveCarryOverValue } from './resolve-carry-over-value';
import { resolveValue } from './resolve-value';

/**
 * The bound the markup holds, as the caller states it.
 *
 * A number or a `Date` is the bound the form applies while the value node
 * gives none. `null` states that the markup holds no bound, so the field is
 * unbounded. `undefined` states nothing, which is what a field that has not
 * moved its bounds to the template passes: its markup usually holds a bound,
 * and the tooltip must not claim otherwise.
 */
export type MarkupFallback = number | Date | null | undefined;

/**
 * What a line says about the markup bound, for a node that gives no value.
 *
 * Each of the three states reads differently, because each means something
 * different to a reader who wants to know what the form applies.
 */
const marksMarkupBound = (fallback: MarkupFallback): string => {
  if (fallback === undefined) return 'the markup bound applies';
  if (fallback === null) return 'the field has no bound';
  return `the markup bound of ${formatValue(fallback)} applies`;
};

/** What a line says before that, where the markup states it holds no bound. */
const andNoMarkupBound = (fallback: MarkupFallback): string => (fallback === null ? ', and no markup bound,' : ',');

/**
 * The line a missing node gives. It says 'no rule', not 'no node', because
 * every other default line in the tooltip says 'no rule' and a reader of the
 * tooltip has no reason to tell a rule tree from a value tree.
 */
const missingNode = (fallback: MarkupFallback): string =>
  `no rule${fallback === null ? ' and no markup bound' : ''}, so ${marksMarkupBound(fallback)}`;

/** What a line adds after the value when the node gives the form no bound. */
const fallsBack = (fallback: MarkupFallback): string =>
  `${andNoMarkupBound(fallback)} so ${marksMarkupBound(fallback)}`;

/**
 * Which of the two readers the walk asks, and what a line says when the reader
 * gets no value. The bounds and the carry-over walk the same tree, but each
 * reader accepts different values, and each has a different result when it
 * gets no value. See Two readers over one tree in form-rule-language.md.
 */
interface ValueReader {
  resolve: (node: ValueNode, context: RuleContext) => unknown;
  /** The line for a missing node. */
  missing: string;
  /** What a line adds after the value when the node gives none. */
  fallsBack: string;
  /** What a line says after 'no else branch'. */
  noBranch: string;
}

const boundReader = (fallback: MarkupFallback): ValueReader => ({
  resolve: resolveValue,
  missing: missingNode(fallback),
  fallsBack: fallsBack(fallback),
  noBranch: marksMarkupBound(fallback),
});

const CARRY_OVER_READER: ValueReader = {
  resolve: resolveCarryOverValue,
  missing: 'no rule, so nothing carries over',
  fallsBack: ', so nothing carries over',
  noBranch: 'nothing carries over',
};

/**
 * DEV-ONLY. Explains how a value tree reached its number or its date. It is the
 * value-tree twin of `explainLabel`: it walks the branch that was taken, and it
 * asks the rule tree the same questions the resolver asked.
 *
 * It does not change how a value resolves. `resolveValue` stays the one answer,
 * and this describes it. Delete this with the rest of the form-rule debug UI.
 *
 * A missing node gives one line that says the markup bound applies, because a
 * tooltip that stops with no reason reads as a bug in the tooltip.
 *
 * `fallback` is the bound the markup holds, which the form applies whenever
 * this tree gives no value. The caller passes the same number it gives
 * `getFieldMin` or `getFieldMax`, and every line that falls back names it. The
 * number reaches the tooltip no other way, so a reader cannot otherwise see
 * which bound the form applies. See MarkupFallback for the three states, and
 * for why 'the caller states none' and 'the caller states nothing' must read
 * differently.
 */
export function explainValue(
  node: ValueNode | undefined,
  context: RuleContext,
  fallback?: MarkupFallback
): RuleExplanationLine[] {
  return explainWith(node, context, boundReader(fallback));
}

/**
 * DEV-ONLY. Explains how a value tree reached the value a carry-over writes.
 * It is the twin of `explainValue` for the carry-over block of the tooltip.
 *
 * It asks `resolveCarryOverValue`, so a string, a boolean and an option read
 * as values, where the bounds read them as no value. A node that gives nothing
 * says nothing carries over, because a carry-over has no markup to fall back
 * to. Delete this with the rest of the form-rule debug UI.
 */
export function explainCarryOverValue(node: ValueNode | undefined, context: RuleContext): RuleExplanationLine[] {
  return explainWith(node, context, CARRY_OVER_READER);
}

function explainWith(node: ValueNode | undefined, context: RuleContext, reader: ValueReader): RuleExplanationLine[] {
  if (node === undefined) {
    return [{ type: 'default', detail: reader.missing, result: false, depth: 0 }];
  }
  return explainValueNode(node, context, reader, 0);
}

function explainValueNode(
  node: ValueNode,
  context: RuleContext,
  reader: ValueReader,
  depth: number
): RuleExplanationLine[] {
  switch (node.type) {
    case 'const': {
      // A node the resolver cannot read gives no value, and the line says so.
      // The detail names the value the template holds either way, so a reader
      // sees the text that failed as well as the failure.
      const resolved = reader.resolve(node, context);
      const detail =
        resolved === undefined
          ? `${formatTemplateValue(node.value)}, unreadable${reader.fallsBack}`
          : formatTemplateValue(node.value);
      return [{ type: node.type, detail, result: resolved !== undefined, depth }];
    }
    case 'reportField': {
      // The line names the path and the value the node read, in the shape the
      // rule lines use. A field the user has not filled reads '(unset)', which
      // tells a bound the template lost from a bound the field has yet to give.
      const resolved = reader.resolve(node, context);
      const read = `${node.path} = ${formatValue(readReportField(context, node.path))}`;
      return [
        {
          type: node.type,
          detail: resolved === undefined ? `${read}${reader.fallsBack}` : read,
          result: resolved !== undefined,
          depth,
        },
      ];
    }
    case 'carryOverField': {
      // The same shape the `reportField` line takes, and the word 'previous'
      // in front of the path. The two paths look alike and reach different
      // reports, so a reader needs the tooltip to say which one this read.
      const resolved = reader.resolve(node, context);
      const read = `previous ${node.path} = ${formatValue(readCarryOverField(context, node.path))}`;
      return [
        {
          type: node.type,
          detail: resolved === undefined ? `${read}${reader.fallsBack}` : read,
          result: resolved !== undefined,
          depth,
        },
      ];
    }
    case 'columnSum': {
      // The line names the path and the sum, so a reader sees which table the
      // node read as well as the answer. A path that reaches no table reads
      // '(none)'.
      const resolved = reader.resolve(node, context);
      const sum = `sum of ${node.path} = ${resolved === undefined ? '(none)' : formatValue(resolved)}`;
      return [
        {
          type: node.type,
          detail: resolved === undefined ? `${sum}${reader.fallsBack}` : sum,
          result: resolved !== undefined,
          depth,
        },
      ];
    }
    case 'vesselField': {
      // The same shape the `vesselFieldTruthy` rule line takes. The line names
      // the value the node read, so a reader sees why a string or a missing
      // value gives no bound.
      const resolved = reader.resolve(node, context);
      const read = `${node.path} = ${formatValue(readVesselField(context, node.path))}`;
      return [
        {
          type: node.type,
          detail: resolved === undefined ? `${read}${reader.fallsBack}` : read,
          result: resolved !== undefined,
          depth,
        },
      ];
    }
    case 'dateOffset':
      // The amount and the unit, then the new date. A mismatch shows the same
      // two sides below the line, which is how to see which one was the wrong
      // kind.
      return explainCalculation(
        node,
        [node.value, node.add],
        ([, amount]) => `add ${amount} ${node.unit}`,
        context,
        reader,
        depth
      );
    case 'sum':
      return explainCalculation(node, node.values, (values) => values.join(' + '), context, reader, depth);
    case 'difference':
      return explainCalculation(
        node,
        [node.value, ...node.subtract],
        (values) => values.join(' - '),
        context,
        reader,
        depth
      );
    case 'product':
      return explainCalculation(node, node.values, (values) => values.join(' × '), context, reader, depth);
    case 'round': {
      // The direction, and the places where the node rounds to more than a
      // whole number, then the value before and after.
      const places = node.places ? ` to ${node.places} place${node.places === 1 ? '' : 's'}` : '';
      return explainCalculation(
        node,
        [node.value],
        ([value]) => `${node.direction}${places} ${value}`,
        context,
        reader,
        depth
      );
    }
    case 'firstValue': {
      // The line names which value the node took, counting from 1, and every
      // value shows below it, so a reader sees which ones gave none.
      const values = node.values.map((value) => reader.resolve(value, context));
      const taken = values.findIndex((value) => value !== undefined);
      const parent: RuleExplanationLine = {
        type: node.type,
        detail: taken === -1 ? `no value${reader.fallsBack}` : `value ${taken + 1} = ${formatValue(values[taken])}`,
        result: taken !== -1,
        depth,
      };
      return [parent, ...node.values.flatMap((value) => explainValueNode(value, context, reader, depth + 1))];
    }
    case 'when': {
      // The same shape the label tree shows: a parent line that names the
      // branch, the rule below it, and the branch it took below that. It walks
      // only the branch it took, as the label tree does.
      const taken = evaluate(node.when, context);
      const parent: RuleExplanationLine = { type: node.type, detail: taken ? 'then' : 'else', result: taken, depth };
      const rule = explainNode(node.when, context, depth + 1);
      const branch = taken ? node.then : node.else;
      // A false rule with no else branch gives no value, so the line says what
      // the form applies instead, in the same way a missing node does.
      const chosen: RuleExplanationLine[] =
        branch === undefined
          ? [
              {
                type: 'none',
                detail: `no else branch, so ${reader.noBranch}`,
                result: false,
                depth: depth + 1,
              },
            ]
          : explainValueNode(branch, context, reader, depth + 1);
      return [parent, ...rule, ...chosen];
    }
    case 'option': {
      // The line names the list and the option found, and the read goes below
      // it, so a reader sees the text that matched no option as well as the
      // failure. The option comes from the carry-over reader in both walks. A
      // bound gets no value from this node, so in a bound walk the line names
      // the option and then says the markup bound applies.
      const option = resolveCarryOverValue(node, context) as SelectOption | undefined;
      const resolved = reader.resolve(node, context);
      const found =
        option === undefined ? `no ${node.options} option matches` : `${node.options} option ${option.name}`;
      const parent: RuleExplanationLine = {
        type: node.type,
        detail: resolved === undefined ? `${found}${reader.fallsBack}` : found,
        result: resolved !== undefined,
        depth,
      };
      return [parent, ...explainValueNode(node.value, context, reader, depth + 1)];
    }
    default:
      // Every node type has a branch above. This guards a new type added
      // without a branch: the compiler fails here.
      return assertNever(node);
  }
}

/**
 * A parent line that shows the calculation with the value of each operand, and
 * a line for each operand below it, so a reader sees the answer and the parts
 * it came from. An operand that gives no number reads '(none)' in the parent
 * line, and its own line below says why. `describe` turns the formatted
 * operands into the left side of the line, for example `12000 + 24 + 2`.
 */
function explainCalculation(
  node: ValueNode,
  operands: ValueNode[],
  describe: (values: string[]) => string,
  context: RuleContext,
  reader: ValueReader,
  depth: number
): RuleExplanationLine[] {
  const resolved = reader.resolve(node, context);
  const values = operands.map((operand) => formatOperand(resolveValue(operand, context)));
  const calculation = `${describe(values)} = ${formatOperand(resolved)}`;
  const parent: RuleExplanationLine = {
    type: node.type,
    detail: resolved === undefined ? `${calculation}${reader.fallsBack}` : calculation,
    result: resolved !== undefined,
    depth,
  };
  return [parent, ...operands.flatMap((operand) => explainValueNode(operand, context, reader, depth + 1))];
}

/** Formats the value of one operand of a calculation. No value reads '(none)'. */
function formatOperand(value: unknown): string {
  return value === undefined ? '(none)' : formatValue(value);
}

function assertNever(node: never): never {
  throw new Error(`Unknown value node type: ${String((node as { type: string }).type)}`);
}

/** Formats the value a node holds. A string keeps its quotes, so a date reads as the text the template holds. */
function formatTemplateValue(value: number | string): string {
  return typeof value === 'string' ? `"${value}"` : String(value);
}
